import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/user/user.service';
import { PlanService } from '../plan.service';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { HttpClient } from '@angular/common/http';
import * as alertifyjs from 'alertifyjs';

interface PlanDetailMetrics {
  creditosActivos: number;
  creditosCompletados: number;
  montoTotalFinanciado: number;
  clientesActivos: number;
  promedioMonto: number;
  tasaAprobacion: number;
  ingresosMes: number;
  proximosVencer: number;
}

@Component({
  selector: 'app-plan-detail',
  templateUrl: './plan-detail.component.html',
  styleUrls: ['./plan-detail.component.css']
})
export class PlanDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  plan: any = null;
  planLoading = false;
  planCode: string = '';

  // Metrics
  metrics: PlanDetailMetrics = {
    creditosActivos: 0,
    creditosCompletados: 0,
    montoTotalFinanciado: 0,
    clientesActivos: 0,
    promedioMonto: 0,
    tasaAprobacion: 0,
    ingresosMes: 0,
    proximosVencer: 0
  };

  // Navigation and view state
  activeSection = 'resumen';
  showHelp: { [key: string]: boolean } = {};
  sections = [
    {
      id: 'resumen',
      name: 'Resumen',
      icon: '📋',
      description: 'Resumen completo de toda la información del plan'
    },
    {
      id: 'etiquetas',
      name: 'Etiquetas',
      icon: '🏷️',
      description: 'Configuración de filtros por etiquetas de usuario'
    },
    {
      id: 'penalidades',
      name: 'Penalidades',
      icon: '💰',
      description: 'Configuración de listas de penalidades (fees) del plan'
    },
    {
      id: 'productos',
      name: 'Productos',
      icon: '🛍️',
      description: 'Identificadores de productos asociados al plan'
    },
    {
      id: 'datos-basicos',
      name: 'Datos Básicos',
      icon: '📝',
      description: 'Información fundamental y configuración del plan'
    },
    {
      id: 'datos-generales',
      name: 'Datos Generales',
      icon: '💰',
      description: 'Configuración financiera y términos de crédito'
    },
    {
      id: 'filtros',
      name: 'Filtros',
      icon: '🔍',
      description: 'Configuración avanzada de elegibilidad del plan'
    },
    {
      id: 'otros',
      name: 'Otros',
      icon: '⚙️',
      description: 'Configuración adicional de documentación y políticas'
    },
    {
      id: 'clientes',
      name: 'Clientes Activos',
      icon: '👥',
      description: 'Usuarios con créditos activos en este plan'
    },
    {
      id: 'historial',
      name: 'Historial de Versiones',
      icon: '📋',
      description: 'Registro de cambios y modificaciones del plan'
    },
    {
      id: 'metricas',
      name: 'Métricas y Análisis',
      icon: '📊',
      description: 'Indicadores de rendimiento y estadísticas'
    }
  ];

  // Clients data
  activeClients: any[] = [];
  clientsLoading = false;

  // Version history
  versionHistory: any[] = [];
  historyLoading = false;

  // Products data
  specificProducts: any[] = [];
  parentProductName: string = '';

  // Insurance data
  insuranceProposals: any[] = [];
  insurers: any[] = [];

  // Fees data
  availableFees: any[] = [];
  feesLoading = false;

  // Current date for template
  currentDate = new Date();

  // Help system
  helpData: any = {};
  helpLoading = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private planService: PlanService,
    private http: HttpClient,
    private merchantService: MerchantService
  ) {}

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.planCode = params['code'];
        if (this.planCode) {
          this.loadPlanDetails();
        }
      });
    
    // Load available fees for display
    this.loadAvailableFees();
    this.cargarComercios();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load complete plan details
   */
  async loadPlanDetails(): Promise<void> {
    try {
      this.planLoading = true;

      // Load plan data
      const plansResponse = await this.userService.getplansall({});
      const plans = plansResponse?.res || [];

      this.plan = plans.find((p: any) => p.code === this.planCode);

      if (!this.plan) {
        alertifyjs.error('Plan no encontrado');
        this.router.navigate(['/console/plans']);
        return;
      }

      // ⚡ OPTIMIZACIÓN: Inicializar invoices con 0 para mostrar la vista inmediatamente
      this.plan.invoices = 0;

      // Load help data first (hardcoded, no async needed)
      this.loadHelpData();

      // Load additional data in parallel (sin loadPlanMetrics para no bloquear)
      await Promise.all([
        this.loadActiveClients(),
        this.loadVersionHistory(),
        this.loadProductsData(),
        this.loadInsuranceData()
      ]);

      // ⚡ Cargar métricas (incluyendo créditos) de forma asíncrona SIN bloquear la vista
      // Esto se ejecuta en background y actualiza el valor cuando termine
      this.loadPlanMetrics().catch(err => {
        console.error('Error loading metrics in background:', err);
      });

      // Check if current active section is visible for this plan type
      // If not, redirect to first visible section
      if (!this.isSectionVisible(this.activeSection)) {
        const visibleSections = this.getVisibleSections();
        if (visibleSections.length > 0) {
          this.activeSection = visibleSections[0].id;
        }
      }

    } catch (error) {
      console.error('Error loading plan details:', error);
      alertifyjs.error('Error al cargar los detalles del plan');
    } finally {
      this.planLoading = false;
    }
  }

  /**
   * Load plan-specific metrics
   */
  async loadPlanMetrics(): Promise<void> {
    try {
      // ⚡ Cargar el conteo de créditos solo para este plan específico
      const invoicesResponse = await this.userService.getplansInvoices({});
      const planInvoices = invoicesResponse?.res?.[this.plan._id] || 0;
      
      // Actualizar el campo invoices en el plan
      this.plan.invoices = planInvoices;

      // Calculate metrics (in real implementation, these would come from specific APIs)
      this.metrics = {
        creditosActivos: planInvoices,
        creditosCompletados: Math.floor(planInvoices * 0.7), // Mock: 70% completed
        montoTotalFinanciado: planInvoices * (this.plan.params?.max_amount_base || 100),
        clientesActivos: Math.floor(planInvoices * 0.8), // Mock: 80% unique clients
        promedioMonto: this.plan.params?.max_amount_base || 100,
        tasaAprobacion: 85 + Math.random() * 10, // Mock: 85-95%
        ingresosMes: planInvoices * 25, // Mock calculation
        proximosVencer: Math.random() * 20 - 10 // Mock: -10% to +10%
      };

    } catch (error) {
      console.error('Error loading plan metrics:', error);
      // En caso de error, usar valor por defecto
      this.plan.invoices = 0;
    }
  }

  /**
   * Load active clients for this plan
   */
  async loadActiveClients(): Promise<void> {
    try {
      this.clientsLoading = true;

      // In real implementation, this would be a specific API call
      // For now, we'll create mock data based on the plan
      this.activeClients = Array.from({ length: Math.min(this.metrics.clientesActivos, 50) }, (_, i) => ({
        id: `client_${i + 1}`,
        name: `Cliente ${i + 1}`,
        email: `cliente${i + 1}@example.com`,
        creditoActivo: (this.plan.params?.min_amount_base || 50) + Math.random() * 100,
        fechaInicio: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000),
        estado: Math.random() > 0.2 ? 'Al día' : 'Atrasado',
        cuotasPagadas: Math.floor(Math.random() * 4) + 1,
        cuotasTotal: 4
      }));

    } catch (error) {
      console.error('Error loading active clients:', error);
    } finally {
      this.clientsLoading = false;
    }
  }

  /**
   * Load version history
   */
  async loadVersionHistory(): Promise<void> {
    try {
      this.historyLoading = true;

      // Use existing version history from plan or create mock data
      this.versionHistory = this.plan.version_change || [
        {
          version: { internal_code: 'v1.0', date_init_version: this.plan.date },
          changes: [{ field: 'Creación inicial', oldValue: '', newValue: 'Plan creado' }],
          user: 'admin'
        }
      ];

    } catch (error) {
      console.error('Error loading version history:', error);
    } finally {
      this.historyLoading = false;
    }
  }

  /**
   * Navigate to section
   */
  navigateToSection(sectionId: string): void {
    // Check if section is visible for this plan type
    if (!this.isSectionVisible(sectionId)) {
      console.warn(`Section ${sectionId} is not visible for this plan type`);
      return;
    }

    this.activeSection = sectionId;

    // Smooth scroll to section
    setTimeout(() => {
      const element = document.getElementById(`section-${sectionId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  /**
   * Toggle help visibility for a section
   */
  toggleHelp(sectionId: string): void {
    this.showHelp[sectionId] = !this.showHelp[sectionId];
  }

  /**
   * Edit plan
   */
  editPlan(): void {
    this.router.navigate(['/console/plans/edit', this.planCode]);
  }

  /**
   * Duplicate plan
   */
  async duplicatePlan(): Promise<void> {
    try {
      const duplicatedPlan = {
        ...this.plan,
        code: `${this.plan.code}_COPY_${Date.now()}`,
        name: `${this.plan.name} (Copia)`,
        date: new Date().toISOString(),
        version: { major: 1, minor: 0 }
      };

      delete duplicatedPlan._id;

      await this.planService.createPlan(duplicatedPlan).toPromise();
      alertifyjs.success('Plan duplicado exitosamente');

    } catch (error) {
      console.error('Error duplicating plan:', error);
      alertifyjs.error('Error al duplicar el plan');
    }
  }

  /**
   * Toggle plan status
   */
  async togglePlanStatus(): Promise<void> {
    try {
      const newStatus = !this.plan.status;
      const newState = newStatus ? 'ACTIVE' : 'INACTIVE';

      // Create a copy of the complete plan object and update status/state
      const completePlan = { ...this.plan };
      completePlan.status = newStatus;
      completePlan.state = newState;

      // Send the complete plan object to the API
      await this.planService.updatePlan(completePlan);

      const actionMessage = newStatus ? 'activado' : 'desactivado';
      alertifyjs.success(`Plan ${actionMessage} exitosamente`);

      // Update the plan status and state locally
      this.plan.status = newStatus;
      this.plan.state = newState;

    } catch (error: any) {
      console.error('Error toggling plan status:', error);
      alertifyjs.error(error?.error?.msg || error?.message || 'Error al cambiar estado del plan');
    }
  }

  /**
   * Go back to plans list
   */
  goBack(): void {
    this.router.navigate(['/console/plans']);
  }

  /**
   * Get payment terms installments as formatted string
   */
  getPaymentTermsText(): string {
    if (!this.plan?.payment_terms || this.plan.payment_terms.length === 0) {
      return 'N/A';
    }

    return this.plan.payment_terms.map((t: any) => t.installments).join(', ');
  }

  /**
   * Get payment frequency text
   */
  getPaymentFrequency(): string {
    if (!this.plan?.payment_terms || this.plan.payment_terms.length === 0) {
      return 'N/A';
    }

    const frequency = this.plan.payment_terms[0]?.frequency?.type;
    switch (frequency) {
      case 'mes': return 'Mensual';
      case 'quincenal': return 'Quincenal';
      case 'semanal':
      case 'semana': return 'Semanal';
      default: return 'Días continuos';
    }
  }

  /**
   * Get product category
   */
  getProductCategory(): string {
    const productType = this.plan?.params?.product_type;
    if (!productType) return 'N/A';

    // Map product types to categories
    const categoryMap: { [key: string]: string } = {
      'CREDIT': 'Crédito',
      'CONTADO': 'Contado',
      'RECHARGE': 'Recargas',
      'SEGUROS_MERCANTIL': 'Seguros',
      'PAYALL': 'PayAll',
      'GIFT_CARD': 'Tarjetas de Regalo'
    };

    return categoryMap[productType] || 'Otros';
  }

  /**
   * Get initial payment value formatted
   */
  getInitialPaymentValue(): string {
    if (!this.plan?.params?.require_initial_payment) {
      return 'N/A';
    }

    const type = this.plan.params.initial_payment_type || 'percentage';
    const value = this.plan.params.initial_payment_value || 0;

    if (type === 'percentage') {
      return `${value}%`;
    } else {
      return this.formatCurrency(value);
    }
  }

  /**
   * Get filter mode text
   */
  getFilterModeText(): string {
    const filterType = this.plan?.params?.filters?.dinamic_code?.person?.type;

    switch (filterType) {
      case 'white_list': return 'Lista Blanca';
      case 'black_list': return 'Lista Negra';
      default: return 'Sin filtro';
    }
  }

  /**
   * Format currency
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format date
   */
  formatDate(date: string | Date): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  /**
   * Get status badge class
   */
  // ── multicomercio ──────────────────────────────────────────────────────────
  // El plan guarda claves de comercio (_id o codigo). Se traen los comercios
  // para poder mostrar el NOMBRE: un plan que dice "aplica a 68f2a1..." no le
  // sirve a nadie. Si el catalogo no carga, se muestra la clave cruda y listo —
  // no se oculta el dato.
  comerciosPorClave: { [clave: string]: string } = {};

  cargarComercios = async (): Promise<void> => {
    try {
      const rep: any = await this.merchantService.getMerchants({});
      for (const c of (rep?.merchants || [])) {
        const nombre = c.name || c.business_name || c.code;
        if (c._id) this.comerciosPorClave[`${c._id}`] = nombre;
        if (c.code) this.comerciosPorClave[`${c.code}`] = nombre;
      }
    } catch (error) {
      console.error('No se pudieron cargar los comercios', error);
    }
  };

  nombreComercio(clave: any): string {
    return this.comerciosPorClave[`${clave}`] || `${clave}`;
  }

  getStatusBadgeClass(status: boolean): string {
    // Las clases salen del sistema de diseno (.kb-badge-* en styles.css) y no
    // de colores crudos: asi la insignia de esta pantalla es la MISMA que la del
    // listado y la del resto de la consola. Inactivo es neutro, no rojo: un plan
    // apagado no es un error.
    return status ? 'kb-badge-exito' : 'kb-badge-neutro';
  }

  /**
   * Get client status badge class
   */
  getClientStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Al día': return 'kb-badge-exito';
      case 'Atrasado': return 'kb-badge-peligro';
      default: return 'kb-badge-neutro';
    }
  }

  /**
   * Get visibility text
   */
  getVisibilityText(visibility: string): string {
    return visibility === 'PRIVATE' ? 'Privado' : 'Público';
  }

  /**
   * Track by function for client list
   */
  trackByClientId(index: number, client: any): any {
    return client.id;
  }

  /**
   * Calculate eligible users count based on tags
   */
  getEligibleUsersCount(): number {
    // Mock calculation - in real implementation would call API
    const baseMockUsers = 1500;
    const tagsCount = this.plan?.params?.filters?.dinamic_code?.person?.list?.length || 0;
    return Math.floor(baseMockUsers * (0.8 - (tagsCount * 0.1)));
  }

  /**
   * Calculate eligible users percentage
   */
  getEligibleUsersPercentage(): number {
    const totalUsers = 2000; // Mock total users
    const eligibleUsers = this.getEligibleUsersCount();
    return Math.round((eligibleUsers / totalUsers) * 100);
  }

  /**
   * Get reach level description
   */
  getReachLevel(): string {
    const percentage = this.getEligibleUsersPercentage();
    if (percentage >= 80) return 'Alto';
    if (percentage >= 50) return 'Medio';
    if (percentage >= 20) return 'Bajo';
    return 'Muy Bajo';
  }

  /**
   * Get user count for specific tag
   */
  getTagUserCount(tag: any): number {
    // Mock calculation - in real implementation would call API
    return Math.floor(Math.random() * 500) + 100;
  }

  /**
   * Get user percentage for specific tag
   */
  getTagUserPercentage(tag: any): number {
    const userCount = this.getTagUserCount(tag);
    const totalUsers = 2000; // Mock total users
    return Math.round((userCount / totalUsers) * 100);
  }

  /**
   * Get dynamic text size class based on content length
   */
  getDynamicTextSizeClass(text: string): string {
    if (!text) return 'text-2xl';

    const length = text.length;

    if (length <= 8) return 'text-2xl lg:text-3xl'; // Texto corto - más grande
    if (length <= 12) return 'text-xl lg:text-2xl'; // Texto medio
    if (length <= 16) return 'text-lg lg:text-xl';  // Texto largo
    return 'text-base lg:text-lg'; // Texto muy largo - más pequeño
  }

  /**
   * Get dynamic currency size class
   */
  getDynamicCurrencySize(amount: number): string {
    const formatted = this.formatCurrency(amount);
    const length = formatted.length;

    if (length <= 10) return 'text-2xl lg:text-3xl font-bold'; // Montos cortos
    if (length <= 15) return 'text-xl lg:text-2xl font-bold';  // Montos medios
    if (length <= 20) return 'text-lg lg:text-xl font-semibold'; // Montos largos
    return 'text-base lg:text-lg font-semibold'; // Montos muy largos
  }

  /**
   * Get dynamic number size class
   */
  getDynamicNumberSize(number: number): string {
    const text = number.toString();
    const length = text.length;

    if (length <= 3) return 'text-2xl lg:text-3xl font-bold'; // 1-999
    if (length <= 5) return 'text-xl lg:text-2xl font-bold';  // 1,000-99,999
    if (length <= 7) return 'text-lg lg:text-xl font-semibold'; // 100,000-9,999,999
    return 'text-base lg:text-lg font-semibold'; // 10,000,000+
  }

  // Product-related methods
  async loadProductsData() {
    try {
      console.log('🔍 Iniciando carga de datos de productos');

      // 1. Cargar todos los productos padre disponibles
      const productTypesResponse = await this.userService.getTypesProductsInvoices();
      this.specificProducts = productTypesResponse?.resp || [];

      // 2. Si tenemos un identificador específico en el plan, buscar su producto padre
      if (this.plan?.params?.product_type) {
        this.parentProductName = await this.getProductNameByIdentifier(this.plan.params.product_type);
      } else {
        this.parentProductName = 'Sin producto asignado';
      }
    } catch (error) {
      console.error('❌ Error loading products data:', error);
      this.specificProducts = [];
      this.parentProductName = 'Error al cargar producto';
    }
  }

  async getProductNameByIdentifier(identifierCode: string): Promise<string> {
    try {
      // Primero buscar en la lista de productos padre ya cargados
      if (this.specificProducts && this.specificProducts.length > 0) {
        // 1. Buscar en productos padre (nivel superior)
        const parentProduct = this.specificProducts.find(p => p.params?.code === identifierCode);
        if (parentProduct) {
          const parentName = parentProduct.params?.name || parentProduct.name;
          if (parentName) {
            return parentName;
          }
        }

        // 2. Buscar en productos anidados (dentro del array products)
        for (const parentProduct of this.specificProducts) {
          if (parentProduct.params?.products && Array.isArray(parentProduct.params.products)) {
            const nestedProduct = parentProduct.params.products.find((p: any) => p.code === identifierCode);
            if (nestedProduct) {
              const productName = nestedProduct.name || nestedProduct.description;
              if (productName) {
                return productName;
              }
            }
          }
        }
      }

      // Si no se encuentra en productos padre, usar getProductsInvoicesFilter como fallback
      const params = { code: identifierCode };
      const result = await this.userService.getProductsInvoicesFilter(params);

      if (result?.resp && result.resp.length > 0) {
        const productData = result.resp[0];
        const productName = productData.params?.name ||
                           productData.name ||
                           productData.type_product;

        if (productName) {
          return productName;
        }
      }

      return ''; // Retornar vacío si no se encuentra en los servicios
    } catch (error) {
      console.error('❌ Error getting product name by identifier:', error);
      return ''; // Retornar vacío en caso de error
    }
  }

  // Método simplificado para obtener detalles del identificador
  async getIdentifierDetails(identifierCode: string): Promise<any> {
    try {
      const params = { code: identifierCode };
      const result = await this.userService.getProductsInvoicesFilter(params);

      if (result?.resp?.length > 0) {
        return result.resp[0];
      }

      return null;
    } catch (error) {
      console.error('Error getting identifier details:', error);
      return null;
    }
  }

  getParentProductIdentifiersCount(identifierCode: string): number {
    // Mock calculation - en producción esto consultaría la API específica
    return Math.floor(Math.random() * 10) + 1;
  }

  getSiblingIdentifiers(currentIdentifierCode: string): any[] {
    // Mock data - en producción esto consultaría la API específica
    return [];
  }

  // Insurance-related methods
  async loadInsuranceData(): Promise<void> {
    try {
      console.log('🔍 Iniciando carga de datos de seguros');

      // Cargar aseguradoras
      const insurersResponse = await this.userService.getInsurers();
      this.insurers = insurersResponse?.resp || [];
      console.log('✅ Aseguradoras cargadas:', this.insurers.length);

      // Si el plan tiene información de seguros, cargar las propuestas
      if (this.plan?.params?.insurance?.product && this.plan?.params?.insurance?.brokerage) {
        const productCode = this.plan.params.insurance.product;
        const insuranceCode = this.plan.params.insurance.brokerage;
        
        console.log('📋 Cargando propuestas para:', { productCode, insuranceCode });
        
        const proposalsResponse = await this.userService.getInsuranceProposals(productCode, insuranceCode);
        
        // Normalizar la respuesta
        if (proposalsResponse && proposalsResponse.resp) {
          this.insuranceProposals = proposalsResponse.resp;
        } else if (Array.isArray(proposalsResponse)) {
          this.insuranceProposals = proposalsResponse;
        } else {
          this.insuranceProposals = [];
        }
        
        console.log('✅ Propuestas cargadas:', this.insuranceProposals.length);
      }
    } catch (error) {
      console.error('❌ Error loading insurance data:', error);
      this.insurers = [];
      this.insuranceProposals = [];
    }
  }

  getInsuranceProposalName(proposalId: string | number): string {
    if (!proposalId) {
      return '-';
    }

    // Primero intentar obtener el proposal_name directamente del plan
    if (this.plan?.params?.insurance?.proposal_name) {
      return this.plan.params.insurance.proposal_name;
    }

    // Si no existe proposal_name, buscar en la lista de propuestas (retrocompatibilidad)
    const proposal = this.insuranceProposals.find(p => p.id == proposalId);
    
    if (proposal && (proposal.name || proposal.nombre)) {
      // Si hay mtPrima, construir el formato "Nombre - Prima"
      const mtPrima = this.plan?.params?.insurance?.mtPrima;
      if (mtPrima) {
        return `${proposal.name || proposal.nombre} - ${mtPrima}`;
      }
      return proposal.name || proposal.nombre;
    }

    return '-';
  }

  getInsurerName(insurerCode: string): string {
    if (!insurerCode) {
      return 'Aseguradora no especificada';
    }

    // Buscar la aseguradora por código
    const insurer = this.insurers.find(i => i.code === insurerCode);
    
    if (insurer) {
      return insurer.name || insurer.nombre || insurerCode;
    }

    return insurerCode;
  }

  getPlansUsingIdentifier(identifierCode: string): number {
    // Mock data - en producción esto vendría de una API específica
    // Se podría implementar un servicio que cuente planes por product_type
    const usageMap: { [key: string]: number } = {
      'CREDIT': 12,
      'CREDIT-PREMIUM': 5,
      'CREDIT-EXPRESS': 8,
      'RECHARGE': 25,
      'RECHARGE-PREMIUM': 3,
      'RECHARGE-EXPRESS': 0,
      'RECHARGE-BULK': 7,
      'SEGUROS_MERCANTIL': 4,
      'SEGUROS_CARACAS': 2
    };

    return usageMap[identifierCode] || 0;
  }

  getGroupedProducts(): any[] {
    // Mock data simplificado - en producción esto vendría de APIs específicas
    return [
      {
        name: 'Créditos',
        identifiers: [
          { code: 'CREDIT', name: 'Crédito Básico', status: true },
          { code: 'CREDIT-PREMIUM', name: 'Crédito Premium', status: true }
        ]
      },
      {
        name: 'Recargas',
        identifiers: [
          { code: 'RECHARGE', name: 'Recarga Básica', status: true },
          { code: 'RECHARGE-PREMIUM', name: 'Recarga Premium', status: true }
        ]
      }
    ];
  }

  hasSelectedIdentifier(identifiers: any[]): boolean {
    if (!this.plan?.params?.product_type || !identifiers) {
      return false;
    }

    return identifiers.some((identifier: any) =>
      identifier.code === this.plan.params?.product_type
    );
  }

  getIdentifierUsageLevel(): number {
    if (!this.plan?.params?.product_type) return 0;

    const totalPlans = this.getPlansUsingIdentifier(this.plan.params.product_type);
    const maxExpectedUsage = 50; // Máximo esperado de planes por identificador

    return Math.min(Math.round((totalPlans / maxExpectedUsage) * 100), 100);
  }

  getProductUsageCount(productCode: string): number {
    // Mock calculation - in real implementation, this would query the database
    return Math.floor(Math.random() * 50) + 1;
  }

  getProductTypeCount(productTypeCode: string): number {
    // Mock calculation - en producción esto consultaría la API específica
    return Math.floor(Math.random() * 10) + 1;
  }

  getPlansWithProductType(productType: string): number {
    // Mock calculation - en producción esto consultaría la base de datos
    const baseCount = productType === 'CREDIT' ? 25 :
                     productType === 'RECHARGE' ? 15 :
                     productType === 'SEGUROS_MERCANTIL' ? 8 : 5;
    return baseCount + Math.floor(Math.random() * 10);
  }

  getTotalProductUsage(): number {
    // Mock calculation for total usage percentage
    if (!this.plan?.params?.product_type) return 0;

    const usageMap: { [key: string]: number } = {
      'CREDIT': 75,
      'RECHARGE': 60,
      'SEGUROS_MERCANTIL': 45,
      'SEGUROS_CARACAS': 40
    };

    return usageMap[this.plan.params.product_type] || 30;
  }

  /**
   * Load help data from JSON file
   */
  loadHelpData(): void {
    // Datos de ayuda hardcodeados que funcionan
    this.helpData = {
      resumen: {
        title: "Vista de Resumen del Plan",
        description: "Información consolidada de todas las características y configuraciones del plan.",
        sections: [
          {
            title: "Información Incluida",
            items: [
              { label: "Datos Básicos", description: "Nombre, código, tipo y estado del plan" },
              { label: "Configuración Financiera", description: "Montos, cuotas y términos de crédito" },
              { label: "Productos", description: "Identificadores y tipos de productos asociados" },
              { label: "Filtros", description: "Etiquetas y restricciones de acceso" },
              { label: "Métricas", description: "Estadísticas actuales de uso del plan" },
              { label: "Configuración Adicional", description: "Pagarés y políticas especiales" }
            ]
          },
          {
            title: "Beneficios del Resumen",
            items: [
              { label: "Vista Completa", description: "Toda la información en una sola pantalla" },
              { label: "Formato Minimalista", description: "Datos organizados de manera clara y concisa" },
              { label: "Acceso Rápido", description: "Información clave sin navegar entre secciones" }
            ]
          }
        ],
        tip: {
          icon: "📋",
          title: "Tip",
          text: "Usa el resumen para obtener una visión general antes de editar secciones específicas"
        }
      },
      etiquetas: {
        title: "Sistema de Etiquetas",
        description: "Las etiquetas permiten segmentar usuarios para controlar el acceso a planes específicos.",
        sections: [
          {
            title: "Modos de Filtrado",
            items: [
              { label: "Lista Blanca", description: "Solo usuarios CON estas etiquetas pueden acceder al plan" },
              { label: "Lista Negra", description: "Solo usuarios SIN estas etiquetas pueden acceder al plan" }
            ]
          },
          {
            title: "Ejemplos de Etiquetas",
            items: [
              { label: "EMPLEADO_PUBLICO", description: "Funcionarios del sector público" },
              { label: "CLIENTE_PREMIUM", description: "Clientes con historial crediticio excelente" },
              { label: "MOROSO", description: "Clientes con pagos en mora" }
            ]
          }
        ],
        tip: {
          icon: "💡",
          title: "Tip",
          text: "Sin etiquetas = Todos los usuarios pueden acceder"
        }
      },
      filtros: {
        title: "Sistema de Filtros Avanzados",
        description: "Los filtros controlan de manera granular qué usuarios y en qué tiendas está disponible este plan.",
        sections: [
          {
            title: "Tipos de Filtros",
            items: [
              { label: "Persona", description: "Filtra por tipo de cliente (Natural/Jurídica)" },
              { label: "Tiendas", description: "Controla en qué puntos de venta está disponible" },
              { label: "Códigos Dinámicos", description: "Filtros avanzados por etiquetas específicas" }
            ]
          },
          {
            title: "Funcionamiento",
            items: [
              { label: "Lista Blanca", description: "Solo permite acceso CON los criterios especificados" },
              { label: "Lista Negra", description: "Permite acceso EXCEPTO con los criterios especificados" },
              { label: "Combinación", description: "Los filtros se combinan para mayor precisión" }
            ]
          }
        ],
        tip: {
          icon: "🎯",
          title: "Tip",
          text: "Combina múltiples filtros para un control granular de elegibilidad"
        }
      },
      productos: {
        title: '¿Cómo funcionan los Productos?',
        icon: 'fas fa-info-circle',
        color: 'green',
        description: 'Los identificadores determinan a qué productos se aplica este plan.',
        sections: [
          {
            title: 'Tipos Disponibles',
            items: [
              { code: 'CREDIT', description: 'Productos de crédito básico' },
              { code: 'RECHARGE', description: 'Productos de recarga' },
              { code: 'SEGUROS_MERCANTIL', description: 'Seguros del Banco Mercantil' }
            ]
          },
          {
            title: 'Funcionamiento',
            items: [
              { label: 'Restricción', description: 'Solo se puede asignar UN identificador por plan' },
              { label: 'Herencia', description: 'El plan hereda las características del producto' }
            ]
          }
        ],
        tip: { icon: '💡', title: 'Tip', text: 'Solo se puede asignar UN identificador por plan' }
      },
      'datos-basicos': {
        title: '¿Qué son los Datos Básicos?',
        icon: 'fas fa-info-circle',
        color: 'purple',
        description: 'Define la identidad y configuración fundamental del plan.',
        sections: [
          {
            title: 'Campos Principales',
            items: [
              { label: 'Nombre', description: 'Identificación amigable del plan', example: 'Plan Empleados 2024' },
              { label: 'Código', description: 'Identificador único inmutable', example: 'EMP_2024_V1' },
              { label: 'Descripción', description: 'Detalles del plan para clientes' }
            ]
          },
          {
            title: 'Estados del Plan',
            items: [
              { label: 'Activo', description: 'Visible y disponible para clientes', color: 'green' },
              { label: 'Inactivo', description: 'Oculto para clientes', color: 'red' }
            ]
          }
        ],
        warning: { icon: '⚠️', title: 'Importante', text: 'El código NO se puede cambiar una vez creado' }
      },
      'datos-generales': {
        title: '¿Cómo configurar los Datos Generales?',
        icon: 'fas fa-info-circle',
        color: 'orange',
        description: 'Configuración financiera y términos de crédito del plan.',
        sections: [
          {
            title: 'Pago Inicial',
            items: [
              { label: 'Requiere inicial', description: 'Si el plan exige pago inicial' },
              { label: 'Tipo de cálculo', description: 'Porcentaje o monto fijo', example: '20% o $50' },
              { label: 'Recalcular cuotas', description: 'Si ajusta cuotas tras el inicial' }
            ]
          },
          {
            title: 'Plazos y Cuotas',
            items: [
              { label: 'Opciones de cuotas', description: 'Cantidad de cuotas disponibles', example: '2, 3, 4 cuotas' },
              { label: 'Frecuencia', description: 'Periodicidad de pago', example: 'Mensual, quincenal' }
            ]
          },
          {
            title: 'Límites de Cupo',
            items: [
              { label: 'Cupo mínimo/máximo', description: 'Rango de cupo asignable' },
              { label: 'Monto mín/máx a financiar', description: 'Límites por transacción' }
            ]
          }
        ],
        examples: [
          {
            title: 'Ejemplo Plan Empleados',
            details: [
              'Inicial: 20% del monto',
              'Cuotas: 2, 3, 4 opciones',
              'Cupo: $500 - $2,000'
            ]
          }
        ],
        tip: { icon: '💡', title: 'Tip', text: 'Define términos de crédito y límites según el perfil de usuario' }
      },
      'otros': {
        title: '¿Qué configurar en Otros?',
        icon: 'fas fa-info-circle',
        color: 'yellow',
        description: 'Configuración adicional de documentación legal y políticas de cobranza.',
        sections: [
          {
            title: 'Documentación Legal',
            items: [
              { label: 'Pagaré Electrónico', description: 'Genera automáticamente documento digital' },
              { label: 'Pagaré Físico', description: 'Requiere documento físico firmado' }
            ]
          },
          {
            title: 'Políticas de Cobranza',
            items: [
              { label: 'Diferir cuotas en feriados', description: 'Mueve automáticamente las cuotas de fines de semana y feriados al siguiente día hábil' },
              { label: 'Permitir en cualquier día', description: 'Las fechas pueden caer en cualquier día si está desactivado' }
            ]
          }
        ],
        examples: [
          {
            title: 'Configuración Recomendada',
            details: [
              'Pagaré Electrónico: Activado (más ágil)',
              'Pagaré Físico: Desactivado (opcional)',
              'Feriados: Desactivado (evita confusiones)'
            ]
          }
        ],
        tip: { icon: '⚖️', title: 'Legal', text: 'Los pagarés son documentos legales que respaldan el crédito' }
      },
      'clientes': {
        title: '¿Cómo interpretar los Clientes?',
        icon: 'fas fa-info-circle',
        color: 'indigo',
        description: 'Usuarios que actualmente tienen créditos activos con este plan.',
        sections: [
          {
            title: 'Estados de Cliente',
            items: [
              { label: 'Al día', description: 'Pagos al corriente', color: 'green' },
              { label: 'Atrasado', description: 'Pagos en mora', color: 'red' }
            ]
          },
          {
            title: 'Información Mostrada',
            items: [
              { label: 'Crédito activo', description: 'Monto actual del crédito' },
              { label: 'Progreso', description: 'Cuotas pagadas vs total' },
              { label: 'Fecha inicio', description: 'Cuándo se otorgó el crédito' }
            ]
          }
        ],
        tip: { icon: '📊', title: 'Análisis', text: 'Evalúa el rendimiento y riesgo del plan' }
      },
      'historial': {
        title: '¿Para qué sirve el Historial?',
        icon: 'fas fa-info-circle',
        color: 'gray',
        description: 'Registro completo de cambios y modificaciones del plan.',
        sections: [
          {
            title: 'Tipos de Cambios',
            items: [
              { label: 'Modificaciones', description: 'Cambios en configuración' },
              { label: 'Versiones', description: 'Nuevas versiones del plan' },
              { label: 'Estados', description: 'Activación/desactivación' }
            ]
          },
          {
            title: 'Información de Auditoría',
            items: [
              { label: 'Fecha y hora', description: 'Cuándo se realizó el cambio' },
              { label: 'Usuario', description: 'Quién realizó la modificación' },
              { label: 'Detalles', description: 'Qué campos se modificaron' }
            ]
          }
        ],
        tip: { icon: '🔍', title: 'Auditoría', text: 'Esencial para cumplimiento y trazabilidad' }
      },
      'metricas': {
        title: '¿Cómo interpretar las Métricas?',
        icon: 'fas fa-info-circle',
        color: 'teal',
        description: 'Indicadores de rendimiento y estadísticas del plan.',
        sections: [
          {
            title: 'Indicadores Clave',
            items: [
              { label: 'Tasa de aprobación', description: 'Porcentaje de solicitudes aprobadas', color: 'green' },
              { label: 'Promedio por crédito', description: 'Monto promedio otorgado' },
              { label: 'Ingresos del mes', description: 'Ingresos generados por el plan' }
            ]
          },
          {
            title: 'Interpretación',
            items: [
              { label: 'Verde', description: 'Indicadores positivos', color: 'green' },
              { label: 'Rojo', description: 'Requiere atención', color: 'red' },
              { label: 'Tendencias', description: 'Observa cambios en el tiempo' }
            ]
          }
        ],
        tip: { icon: '📈', title: 'Optimización', text: 'Usa estas métricas para mejorar el rendimiento del plan' }
      }
    };
  }

  // Filters-related methods
  /**
   * Check if plan has any filters configured
   */
  hasAnyFilters(): boolean {
    const filters = this.plan?.params?.filters;
    if (!filters) return false;

    return !!(
      filters.general?.person?.audience ||
      filters.general?.invoices?.shop?.list?.length > 0 ||
      filters.general?.invoices?.merchant?.list?.length > 0 ||
      filters.dinamic_code?.person?.list?.length > 0 ||
      filters.dinamic_code?.other?.list?.length > 0
    );
  }

  /**
   * Get count of active filters
   */
  getActiveFiltersCount(): number {
    const filters = this.plan?.params?.filters;
    if (!filters) return 0;

    let count = 0;
    if (filters.general?.person?.audience) count++;
    if (filters.general?.invoices?.shop?.list?.length > 0) count++;
    // Acotar por comercio ES un filtro y tiene que contarse: si no, un plan
    // limitado a un comercio se muestra como «sin filtros» y nadie entiende por
    // que no le aparece a los demas.
    if (filters.general?.invoices?.merchant?.list?.length > 0) count++;
    if (filters.dinamic_code?.person?.list?.length > 0) count++;
    if (filters.dinamic_code?.other?.list?.length > 0) count++;

    return count;
  }

  /**
   * Get filtered eligible users count (considering all filters)
   */
  getFilteredEligibleUsersCount(): number {
    const baseUsers = 2000; // Mock total users
    const activeFilters = this.getActiveFiltersCount();

    // Each filter reduces the eligible audience
    const reductionFactor = Math.pow(0.7, activeFilters);
    return Math.floor(baseUsers * reductionFactor);
  }

  /**
   * Get available stores count
   */
  getAvailableStoresCount(): number {
    const shopFilter = this.plan?.params?.filters?.general?.invoices?.shop;
    const totalStores = 150; // Mock total stores

    if (!shopFilter) return totalStores;

    if (shopFilter.type === 'white_list') {
      return shopFilter.list?.length || 0;
    } else if (shopFilter.type === 'black_list') {
      return totalStores - (shopFilter.list?.length || 0);
    }

    return totalStores;
  }

  /**
   * Get filtered reach level
   */
  getFilteredReachLevel(): string {
    const eligibleUsers = this.getFilteredEligibleUsersCount();
    const totalUsers = 2000; // Mock total users
    const percentage = (eligibleUsers / totalUsers) * 100;

    if (percentage >= 80) return 'Alto';
    if (percentage >= 50) return 'Medio';
    if (percentage >= 20) return 'Bajo';
    return 'Muy Bajo';
  }

  /**
   * Determine if plan is a credit plan or cash plan
   */
  isPlanCredit(): boolean {
    // DEBUG: Log para verificar la lógica
    console.log('🔍 isPlanCredit - DEBUG INFO:');
    console.log('📋 Plan exists:', !!this.plan);
    console.log('📋 Plan params type:', this.plan?.params?.type);
    console.log('📋 Payment terms:', this.plan?.payment_terms);
    console.log('📋 Payment terms length:', this.plan?.payment_terms?.length);

    // Primero revisar el campo params.type que es el definitivo
    // Soporta 3 tipos: 'CONTADO', 'CUOTA', 'CREDITO'
    // Tanto 'CUOTA' como 'CREDITO' se consideran planes de crédito (tienen cuotas)
    if (this.plan?.params?.type) {
      const paramType = this.plan.params.type.toUpperCase();
      const isCredit = paramType === 'CREDITO' || paramType === 'CUOTA';
      console.log('💳 Using params.type:', paramType, '- Is Credit:', isCredit);
      return isCredit;
    }

    // Si no hay params.type, por defecto es CRÉDITO
    if (!this.plan?.params?.type) {
      console.log('💳 No params.type found - Default to CREDIT: true');
      return true; // Default: CRÉDITO
    }

    // Fallback: Un plan es de crédito si tiene términos de pago con más de 1 cuota
    // o si tiene múltiples términos de pago
    if (!this.plan?.payment_terms || this.plan.payment_terms.length === 0) {
      console.log('💳 No payment_terms - Default to CREDIT: true');
      return true; // Sin términos de pago = plan de crédito por defecto
    }

    // Si hay múltiples términos de pago, es crédito
    if (this.plan.payment_terms.length > 1) {
      console.log('💳 Multiple payment terms - Is CREDIT: true');
      return true;
    }

    // Si el único término tiene más de 1 cuota, es crédito
    const singleTerm = this.plan.payment_terms[0];
    const isCreditPlan = singleTerm.installments > 1;
    console.log('💳 Single term with installments:', singleTerm.installments, '- Is CREDIT:', isCreditPlan);
    return isCreditPlan;
  }

  /**
   * Get plan type text
   * Retorna el tipo de plan en formato legible
   */
  getPlanTypeText(): string {
    if (!this.plan?.params?.type) {
      return 'Crédito'; // Default
    }
    
    const paramType = this.plan.params.type.toUpperCase();
    
    if (paramType === 'CONTADO') {
      return 'Contado';
    } else if (paramType === 'CUOTA') {
      return 'Cuotas';
    } else {
      return 'Crédito';
    }
  }

  /**
   * Get visible sections based on plan type
   * For cash plans, hide "datos-generales" section
   * Hide "clientes" and "metricas" sections per user request
   */
  getVisibleSections(): any[] {
    if (!this.plan) {
      return this.sections;
    }

    // Filter out sections that should be hidden
    let filteredSections = this.sections.filter(section => {
      // Hide "datos-generales" for cash plans
      if (section.id === 'datos-generales' && !this.isPlanCredit()) {
        return false;
      }

      // Hide "clientes" and "metricas" sections
      if (section.id === 'clientes' || section.id === 'metricas') {
        return false;
      }

      return true;
    });

    return filteredSections;
  }

  /**
   * Check if a section should be visible based on plan type
   */
  isSectionVisible(sectionId: string): boolean {
    if (!this.plan) {
      return true;
    }

    // Hide "datos-generales" for cash plans
    if (sectionId === 'datos-generales' && !this.isPlanCredit()) {
      return false;
    }

    // Hide "clientes" and "metricas" sections
    if (sectionId === 'clientes' || sectionId === 'metricas') {
      return false;
    }

    return true;
  }

  /**
   * Load available fees from API
   */
  async loadAvailableFees(): Promise<void> {
    try {
      this.feesLoading = true;
      const response = await this.userService.getAllFees();
      
      if (response?.success && Array.isArray(response.data)) {
        this.availableFees = response.data;
        console.log('✅ Fees cargados en plan-detail:', this.availableFees.length);
      } else if (response && Array.isArray(response)) {
        this.availableFees = response;
      } else if (response?.resp && Array.isArray(response.resp)) {
        this.availableFees = response.resp;
      }
    } catch (error) {
      console.error('❌ Error loading fees:', error);
      this.availableFees = [];
    } finally {
      this.feesLoading = false;
    }
  }

  /**
   * Get fee name by code_concept
   */
  getFeeName(code_concept: string): string {
    const fee = this.availableFees.find(f => f.code_concept === code_concept);
    return fee ? fee.name : code_concept;
  }

  /**
   * Get white list fees from plan
   */
  getWhiteListFees(): string[] {
    try {
      const feesApply = this.plan?.params?.fees?.apply;
      if (!feesApply || !Array.isArray(feesApply)) {
        return [];
      }
      
      const whiteListObj = feesApply.find((item: any) => item.type === 'white_list');
      return whiteListObj?.list || [];
    } catch (error) {
      console.error('Error getting white list fees:', error);
      return [];
    }
  }

  /**
   * Get black list fees from plan
   */
  getBlackListFees(): string[] {
    try {
      const feesApply = this.plan?.params?.fees?.apply;
      if (!feesApply || !Array.isArray(feesApply)) {
        return [];
      }
      
      const blackListObj = feesApply.find((item: any) => item.type === 'black_list');
      return blackListObj?.list || [];
    } catch (error) {
      console.error('Error getting black list fees:', error);
      return [];
    }
  }
}
